import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  DropdownWrapper,
  OpenedDropdown,
} from "../../../components/table/MatchTable";
import TextInput from "../../../components/inputs/TextInput";
import Button from "../../../components/button";
import VideoSettingsComponent from '../../../components/Events/VideoSettings/VideoSettings';
import { openVideoList } from "../../../helpers/service";

// Style
import {
  BottomFields,
  BottomGoalFields,
  BottomGoalLeft,
  BottomGoalMiddle,
  BottomGoalRight,
  EventsMatchesWrap,
  FieldCount,
  FootballField,
  FootballFieldCount,
  LeftTopField,
  ListOfMatches,
  MiddleTopField,
  RightTopField,
  TopFields,
  TopGoalFields,
  TopGoalLeft,
  TopGoalMiddle,
  TopGoalRight,
} from "../../../style/EventsMatchesStyle";

// Images
import goalFieldBackground from "../../../images/ic_goal.png";
import arrowIconUp from "../../../images/up.svg";
import arrowIconDown from "../../../images/down.svg";
import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  EventCard,
  EventCardWrapper,
  EventContent,
  FootballFielOptions,
  ParametersTabWrapper,
} from "../shared/ui/EventSharedStyles";
import {
  TabContent,
  TabListWrapper,
  TabsWrapper,
  TabTitle,
  TopList,
} from "../../../style/Favorites";
import { SelectDropdown } from "../../../style/Global";
import { TimelineChange } from "../../../components/Events/TimelineChange";
import { mirrorCoordinates } from "../../../helpers";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { useLexicon } from "../../../hooks/useLexicon";
import { ChoosenSelectionsPopover } from "../../../components/Events/ChooseSeasonSelector/ChoosenSelectionsPopover";
import { VideoSettingsWrapper } from '../../../components/Events/VideoSettings/VideoSettings';
import { useSelector } from "react-redux";
import {
  getMarkerEventColor,
} from "../../../helpers/eventMarkerColors";
import WatchEventsButton from "../shared/ui/WatchEventsButton";
import useActionSelection from "../shared/hooks/useActionSelection";
import useTimelineFilter from "../shared/hooks/useTimelineFilter";
import usePlayerSingleSideEventsData from "../shared/hooks/usePlayerSingleSideEventsData";
import useEventMarkerHover from "../shared/hooks/useEventMarkerHover";
import {
  PitchTrackerFrame,
  PitchTrackerSvg,
} from "../shared/ui/EventPitchTrackerStyles";
import EventHalfPitchBaseLayer from "../shared/ui/EventHalfPitchBaseLayer";
import EventHoverTracker from "../shared/ui/EventHoverTracker";
import PitchLoadingOverlay from "../shared/ui/PitchLoadingOverlay";
import SelectedActionsPopoverContent from "../shared/ui/SelectedActionsPopoverContent";
import EventHalfPitchStaticPreview from "../shared/ui/EventHalfPitchStaticPreview";
import {
  convertHalfPitchCoordinates,
  createGoalMouthConverter,
  calculateZonePercentages,
  countHalfPitchZones,
  countGoalMouthZones,
} from "../shared/utils/pitchMath";

export { Dropdown, DropdownButton, DropdownContent, DropdownItem };

const ShotsPlayer = ({
  players,
  paramsShotList,
  selectedMatchIds = [],
}) => {
  const l = useLexicon();
  const { id } = useParams();
  const [spotOn, setSpotOn] = useState(l(1001));
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [openSelect, setOpenSelect] = useState(false);
  const [showDirection, setShowDirection] = useState(false);
  const [selectedZones, setSelectedZones] = useState([]);
  const [zoneOpen, setZoneOpen] = useState(false);
  const user = localStorage.getItem('user_id');

  const dataForTimeLineChange = useRef(null);

  const userState = useSelector((state) => state.user.userProfile);
  // Body part filter for markers_search: [] = all, 1 = right foot, 2 = left foot, 3 = header, 4 = body
  const [bodyPartIds, setBodyPartIds] = useState([]);
  const toggleBodyPart = (id) => {
    setBodyPartIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const {
    checkedIds,
    checkedAction,
    groupedSelectedActions,
    handleCheckboxChange,
    resetActionSelection,
  } = useActionSelection();

  const buildSearchPayload = useCallback(
    ({ markers: markersToSearch }) => {
      const payload = { markers: markersToSearch };
      if (bodyPartIds?.length > 0) {
        payload.body_part_id = bodyPartIds;
      }
      return payload;
    },
    [bodyPartIds]
  );

  const {
    response,
    markerColorMap,
    allMergeMarkers,
    loadingMarkers,
    timeLineRange,
    resetData,
  } = usePlayerSingleSideEventsData({
    id,
    matchesList: selectedMatchIds,
    checkedIds,
    checkedAction,
    mergeOffset: userState?.merge_offset,
    buildSearchPayload,
    searchDeps: [bodyPartIds],
  });

  const { getDebouncedTimelineHandler } = useTimelineFilter({ debounceMs: 500 });

  const {
    hoveredEvent,
    hoveredEventColor,
    hoverCursor,
    handleMarkerHoverStart,
    handleMarkerHoverMove,
    clearMarkerHover,
    resetHover,
  } = useEventMarkerHover();

  const debouncedChangeTimeLineRange = useMemo(
    () =>
      getDebouncedTimelineHandler({
        getEvents: () => dataForTimeLineChange.current?.events || [],
        setDisplayedEvents,
      }),
    [getDebouncedTimelineHandler]
  );

  useEffect(() => {
    dataForTimeLineChange.current = response;
    setDisplayedEvents(response?.events ?? []);
  }, [response]);

  const timelineScopedMergeMarkers = useMemo(() => {
    const sourceMergeMarkers = response?.allMarkers || allMergeMarkers || [];
    if (sourceMergeMarkers.length === 0) {
      return [];
    }

    const displayedEventIds = new Set(
      (displayedEvents || []).map((item) => String(item?.id))
    );
    if (displayedEventIds.size === 0) {
      return [];
    }

    return sourceMergeMarkers.filter((markerId) =>
      displayedEventIds.has(String(markerId))
    );
  }, [allMergeMarkers, displayedEvents, response]);

  useEffect(() => {
    return () => {
      debouncedChangeTimeLineRange?.cancel?.();
    };
  }, [debouncedChangeTimeLineRange]);

  const fieldWidth = 649;
  const fieldHeight = 482;
  const fieldPadding = 0;

  const convertCoordinatesWithPadding = useCallback(
    (coord_x, coord_y) =>
      convertHalfPitchCoordinates({
        coordX: coord_x,
        coordY: coord_y,
        fieldWidth,
        fieldHeight,
      }),
    [fieldHeight, fieldWidth]
  );

  const convertCoordinatesGoalWithPadding = useMemo(
    () =>
      createGoalMouthConverter({
        imageWidthPx: 649,
        imageHeightPx: 482,
        goalWidthMeters: 7.32,
        goalHeightMeters: 2.44,
        verticalPaddingMeters: 1.06,
        horizontalPaddingMeters: 1.34,
      }),
    []
  );

  const counts = useMemo(
    () =>
      countHalfPitchZones({
        events: displayedEvents,
        convertCoordinatesWithPadding,
        mirrorCoordinates,
      }),
    [convertCoordinatesWithPadding, displayedEvents]
  );

  const goalCounts = useMemo(
    () =>
      countGoalMouthZones({
        events: displayedEvents,
        convertCoordinatesGoalWithPadding,
        mirrorCoordinates,
      }),
    [convertCoordinatesGoalWithPadding, displayedEvents]
  );

  const zonePercentages = useMemo(
    () => calculateZonePercentages(counts),
    [counts]
  );

  const goalZonePercentages = useMemo(
    () => calculateZonePercentages(goalCounts),
    [goalCounts]
  );

  const spotList = [
    {
      id: 1,
      name: l(1001),
    },
    {
      id: 2,
      name: l(1002),
    },
  ];

  const resetAllSettings = () => {
    setDisplayedEvents([]);
    resetHover();
    setBodyPartIds([]);
    resetActionSelection();
    resetData();
  };

  const selectDropdownRef = useOutsideClick(() => {
    if (openSelect) {
      setOpenSelect(false);
    }
  });

  const PopoverContent = useMemo(() => {
    if (!groupedSelectedActions) {
      return null;
    }

    return (
      <SelectedActionsPopoverContent
        groupedSelectedActions={groupedSelectedActions}
        l={l}
      />
    );
  }, [groupedSelectedActions, l])

  const handleZoneSelection = (zone) => {
    setSelectedZones(prev => {
      if (prev.includes(zone)) {
        return prev.filter(z => z !== zone);
      }
      return [...prev, zone];
    });
  };

  return (
    <>
      <EventsMatchesWrap>
        <ListOfMatches>
          <EventCardWrapper>
            <h3>
              {l(996)}
              {groupedSelectedActions && (
                <ChoosenSelectionsPopover content={PopoverContent}>Show selections</ChoosenSelectionsPopover>
              )}
            </h3>
            <EventCard style={{ maxHeight: "400px", overflow: "scroll" }}>
              <EventContent>
                <TabsWrapper>
                  <TopList
                    $needBackGroundColor={true}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <TabListWrapper
                      style={{
                        overflow: "visible",
                      }}
                    >
                      <TabTitle>
                        {l(424)}
                        {(groupedSelectedActions?.[424]?.length || 0) > 0 && <span>{groupedSelectedActions?.[424]?.length || 0}</span>}
                      </TabTitle>
                      <TabTitle>
                        {l(18)}
                        {(groupedSelectedActions?.[18]?.length || 0) > 0 && <span>{groupedSelectedActions?.[18]?.length || 0}</span>}
                      </TabTitle>
                    </TabListWrapper>
                  </TopList>
                  <TabContent>
                    <ParametersTabWrapper>
                      {paramsShotList
                        ?.filter(
                          (item) =>
                            item.name.includes("Goal") &&
                            !item.name.includes("%")
                        )
                        ?.map((item) => (
                          <div className="checkboxField" key={item.id}>
                            <label htmlFor={item.id}>
                              <input
                                id={item.id}
                                type="checkbox"
                                checked={checkedIds.includes(item.id)}
                                onChange={() => handleCheckboxChange(item, { title: 'Goals', lexic_id: 424 })}
                              />
                              {l(item.lexic) || item.name}
                            </label>
                          </div>
                        ))}
                    </ParametersTabWrapper>
                  </TabContent>
                  <TabContent>
                    <ParametersTabWrapper>
                      {paramsShotList
                        .filter(
                          (item) =>
                            item.name.includes("Shot") &&
                            !item.name.includes("%")
                        )
                        .map((item, index) => (
                          <div className="checkboxField" key={index}>
                            <label htmlFor={item.id}>
                              <input
                                id={item.id}
                                type="checkbox"
                                checked={checkedIds.includes(item.id)}
                                onChange={() => handleCheckboxChange(item, { title: 'Shots', lexic_id: 18 })}
                              />
                              {l(item.lexic) || item.name}
                            </label>
                          </div>
                        ))}
                    </ParametersTabWrapper>
                  </TabContent>
                </TabsWrapper>
              </EventContent>
            </EventCard>
          </EventCardWrapper>

          <EventCardWrapper>
            <h3>{l(1617)}</h3>
            <EventCard style={{ maxHeight: "400px", overflow: "scroll" }}>
              <EventContent>
                <ParametersTabWrapper>
                  <div className="checkboxField">
                    <label htmlFor="body-part-all">
                      <input
                        id="body-part-all"
                        type="checkbox"
                        checked={bodyPartIds.length === 0}
                        onChange={() => setBodyPartIds([])}
                      />
                      {l(1618)}
                    </label>
                  </div>
                  <div className="checkboxField">
                    <label htmlFor="body-part-left">
                      <input
                        id="body-part-left"
                        type="checkbox"
                        checked={bodyPartIds.includes(2)}
                        onChange={() => toggleBodyPart(2)}
                      />
                      {l(1613)}
                    </label>
                  </div>
                  <div className="checkboxField">
                    <label htmlFor="body-part-right">
                      <input
                        id="body-part-right"
                        type="checkbox"
                        checked={bodyPartIds.includes(1)}
                        onChange={() => toggleBodyPart(1)}
                      />
                      {l(1614)}
                    </label>
                  </div>
                  <div className="checkboxField">
                    <label htmlFor="body-part-header">
                      <input
                        id="body-part-header"
                        type="checkbox"
                        checked={bodyPartIds.includes(3)}
                        onChange={() => toggleBodyPart(3)}
                      />
                      {l(1615)}
                    </label>
                  </div>
                  <div className="checkboxField">
                    <label htmlFor="body-part-body">
                      <input
                        id="body-part-body"
                        type="checkbox"
                        checked={bodyPartIds.includes(4)}
                        onChange={() => toggleBodyPart(4)}
                      />
                      {l(1616)}
                    </label>
                  </div>
                </ParametersTabWrapper>
              </EventContent>
            </EventCard>
          </EventCardWrapper>
        </ListOfMatches>

        <FootballField>
          <FootballFielOptions>
            <VideoSettingsWrapper style={{ zIndex: 60 }}>
              <SelectDropdown>
                <DropdownWrapper
                  ref={selectDropdownRef}
                  style={{ marginBottom: 0, zIndex: 61 }}
                >
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}
                    className="shotsSelector"
                    onClick={() => setOpenSelect(!openSelect)}
                  >
                    <TextInput
                      type="text"
                      value={
                        spotOn ?
                          `${spotOn}`
                          : ''
                      }
                      readOnly
                    />
                    <img
                      src={openSelect ? arrowIconUp : arrowIconDown}
                      alt={arrowIconDown}
                      style={{
                        color: "#fff",
                        position: "absolute",
                        right: 0,
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  {openSelect && (
                    <OpenedDropdown>
                      {spotList?.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => { setSpotOn(item.name); setOpenSelect(false) }}
                        >
                          {item.name}
                        </div>
                      ))}
                    </OpenedDropdown>
                  )}
                </DropdownWrapper>
              </SelectDropdown>
            </VideoSettingsWrapper>
            <Button
              onClick={() => resetAllSettings()}
              title={l(457)}
              size={"normal"}
            />
            <WatchEventsButton
              count={displayedEvents?.length}
              onClick={() =>
                openVideoList(
                  displayedEvents?.map((item) => item.id)?.join(","),
                  `${l(9)} - ${players?.player?.display_name}`,
                  false,
                  timelineScopedMergeMarkers
                )
              }
              onContextMenu={() =>
                openVideoList(
                  displayedEvents?.map((item) => item.id)?.join(","),
                  `${l(9)} - ${players?.player?.display_name}`,
                  false,
                  timelineScopedMergeMarkers
                )
              }
            />
          </FootballFielOptions>
          <VideoSettingsComponent
            showDirection={showDirection}
            setShowDirection={setShowDirection}
            selectedZones={selectedZones}
            handleZoneSelection={handleZoneSelection}
            zoneOpen={zoneOpen}   
            setZoneOpen={setZoneOpen}
            user={user}
            showZones={false}
          />
          <div style={{height: 10}} />
          <PitchLoadingOverlay loading={loadingMarkers}>
            {spotOn === l(1001) ? (
              <PitchTrackerFrame>
                <PitchTrackerSvg
                  width={fieldWidth}
                  height={fieldHeight}
                  viewBox={`0 0 ${fieldWidth} ${fieldHeight}`}
                  preserveAspectRatio="xMidYMid meet"
                  $drawing={false}
                  $grassStripeDirection="horizontal"
                >
                  <EventHalfPitchBaseLayer
                    fieldWidth={fieldWidth}
                    fieldHeight={fieldHeight}
                    fieldPadding={fieldPadding}
                  />
                  {displayedEvents?.map((event) => {
                    const {
                      coord_x,
                      coord_y,
                      coord_x_destination,
                      coord_y_destination,
                    } = mirrorCoordinates(event);
                    const position = convertCoordinatesWithPadding(coord_x, coord_y);
                    const eventColor = getMarkerEventColor(event.id, markerColorMap);
                    const arrowsEnd = convertCoordinatesWithPadding(
                      coord_x_destination,
                      coord_y_destination
                    );
                    const isHovered = hoveredEvent?.id === event.id;

                    return (
                      <React.Fragment key={event.id}>
                        {arrowsEnd?.x > 0 && arrowsEnd.y >= 0 && (
                          <>
                            <defs>
                              <marker
                                id={`arrow-${event.id}`}
                                markerWidth="5"
                                markerHeight="5"
                                refX="0"
                                refY="2"
                                orient="auto"
                              >
                                <path d="M0,0 L0,3 L4,1.5 z" fill="#000" />
                              </marker>
                            </defs>
                            <line
                              x1={position.x}
                              y1={position.y}
                              x2={arrowsEnd.x}
                              y2={arrowsEnd.y}
                              stroke="#000"
                              strokeWidth="2"
                              markerEnd={`url(#arrow-${event.id})`}
                            />
                          </>
                        )}
                        <g
                          className={`event-dot-group ${isHovered ? "is-hovered" : ""}`}
                          onClick={() =>
                            openVideoList(
                              event.id,
                              `${event.action} - ${event?.creator?.display_name}`,
                              true
                            )
                          }
                          onMouseEnter={(nativeEvent) =>
                            handleMarkerHoverStart(event, eventColor, nativeEvent)
                          }
                          onMouseMove={handleMarkerHoverMove}
                          onMouseLeave={clearMarkerHover}
                        >
                          <circle
                            className="event-dot-hover-ring"
                            cx={position.x}
                            cy={position.y}
                            r="9.6"
                            stroke={eventColor}
                          />
                          <circle
                            className="event-dot-core"
                            cx={position.x}
                            cy={position.y}
                            r="5"
                            fill={eventColor}
                          />
                        </g>
                      </React.Fragment>
                    );
                  })}
                </PitchTrackerSvg>
              </PitchTrackerFrame>
            ) : (
              <PitchTrackerFrame>
                <PitchTrackerSvg
                  width={fieldWidth}
                  height={fieldHeight}
                  viewBox={`0 0 ${fieldWidth} ${fieldHeight}`}
                  preserveAspectRatio="xMidYMid meet"
                  $drawing={false}
                >
                  <image
                    href={goalFieldBackground}
                    x={fieldPadding}
                    y={fieldPadding}
                    width={fieldWidth}
                    height={fieldHeight}
                    preserveAspectRatio="xMidYMid slice"
                  />
                  {displayedEvents?.map((event) => {
                    const { gate_coord_x, gate_coord_y } = event;
                    const position = convertCoordinatesGoalWithPadding(
                      gate_coord_x,
                      gate_coord_y
                    );
                    const eventColor = getMarkerEventColor(event.id, markerColorMap);
                    const isHovered = hoveredEvent?.id === event.id;

                    return (
                      <g
                        key={event.id}
                        className={`event-dot-group ${isHovered ? "is-hovered" : ""}`}
                        onClick={() =>
                          openVideoList(
                            event.id,
                            `${event.action} - ${event?.creator?.display_name}`,
                            true
                          )
                        }
                        onMouseEnter={(nativeEvent) =>
                          handleMarkerHoverStart(event, eventColor, nativeEvent)
                        }
                        onMouseMove={handleMarkerHoverMove}
                        onMouseLeave={clearMarkerHover}
                      >
                        <circle
                          className="event-dot-hover-ring"
                          cx={position.x}
                          cy={position.y}
                          r="9.6"
                          stroke={eventColor}
                        />
                        <circle
                          className="event-dot-core"
                          cx={position.x}
                          cy={position.y}
                          r="5"
                          fill={eventColor}
                        />
                      </g>
                    );
                  })}
                </PitchTrackerSvg>
              </PitchTrackerFrame>
            )}
          </PitchLoadingOverlay>

          <EventHoverTracker
            event={hoveredEvent}
            visible={!!hoveredEvent}
            badgeColor={hoveredEventColor}
            cursorX={hoverCursor.x}
            cursorY={hoverCursor.y}
            emptyText={l(464)}
          />

          <TimelineChange
            onChange={debouncedChangeTimeLineRange}
            min={timeLineRange?.min}
            max={timeLineRange?.max}
            styleContainer={{ marginTop: 35, marginBottom: 20 }}
          />

          {spotOn === l(1001) ? (
            <FootballFieldCount>
              <EventHalfPitchStaticPreview
                fieldWidth={fieldWidth}
                fieldHeight={fieldHeight}
                fieldPadding={fieldPadding}
              />

              <FieldCount>
                <TopFields>
                  <LeftTopField>
                    {displayedEvents?.length > 0
                      ? `${zonePercentages.leftTop} %`
                      : "0"}
                  </LeftTopField>
                  <MiddleTopField>
                    {displayedEvents?.length > 0
                      ? `${zonePercentages.middleTop} %`
                      : "0"}
                  </MiddleTopField>
                  <RightTopField>
                    {displayedEvents?.length > 0
                      ? `${zonePercentages.rightTop} %`
                      : "0"}
                  </RightTopField>
                </TopFields>
                <BottomFields>
                  {displayedEvents?.length > 0
                    ? `${zonePercentages.bottom} %`
                    : "0"}
                </BottomFields>
              </FieldCount>
            </FootballFieldCount>
          ) : (
            <FootballFieldCount>
              <img src={goalFieldBackground} alt={goalFieldBackground} />
              <FieldCount>
                <TopGoalFields>
                  <TopGoalLeft>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.leftTop} %`
                      : "0"}
                  </TopGoalLeft>
                  <TopGoalMiddle>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.middleTop} %`
                      : "0"}
                  </TopGoalMiddle>
                  <TopGoalRight>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.rightTop} %`
                      : "0"}
                  </TopGoalRight>
                </TopGoalFields>
                <BottomGoalFields>
                  <BottomGoalLeft>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.leftBottom} %`
                      : "0"}
                  </BottomGoalLeft>
                  <BottomGoalMiddle>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.middleBottom} %`
                      : "0"}
                  </BottomGoalMiddle>
                  <BottomGoalRight>
                    {displayedEvents?.length > 0
                      ? `${goalZonePercentages.rightBottom} %`
                      : "0"}
                  </BottomGoalRight>
                </BottomGoalFields>
              </FieldCount>
            </FootballFieldCount>
          )}
        </FootballField>
      </EventsMatchesWrap>
    </>
  );
};

export default ShotsPlayer;
